import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { CertificateApiService } from 'app/library/certificate/services/certificate.api-service';
import { AddGradedUnits } from 'app/library/graded-unit/actions';
import { IGradedUnitQuery } from 'app/library/graded-unit/interfaces/graded-unit.query.interface';
import { GradedUnit } from 'app/library/graded-unit/models/graded-unit.model';
import { GradingScheme } from 'app/library/grading-scheme/models/grading-scheme.model';
import { GradingSchemeState } from 'app/library/grading-scheme/models/grading-scheme.state';
import { PartialGrade } from 'app/library/partial-grade/models/partial-grade.model';
import { SetParticipantPartialGrades } from 'app/library/participant-partial-grades/actions';
import { IActionWithPayload } from 'app/projects/core/src/lib/interfaces/action_with_payload.interface';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AddFinalGrades,
    AssignFinalGradeCertificate,
    AssignFinalGradeCertificateFailure,
    AssignFinalGradeCertificateSuccess,
    BulkAssignFinalGradeCertificates,
    BulkAssignFinalGradeCertificatesFailure,
    BulkAssignFinalGradeCertificatesSuccess,
    BulkUnassignFinalGradeCertificates,
    BulkUnassignFinalGradeCertificatesFailure,
    BulkUnassignFinalGradeCertificatesSuccess,
    BulkUpdateFinalGrades,
    BulkUpdateFinalGradesFailure,
    BulkUpdateFinalGradesStatus,
    BulkUpdateFinalGradesSuccess,
    DeleteFinalGrade,
    DeleteFinalGradeFailure,
    DeleteFinalGradeSuccess,
    FetchFinalGrade,
    FetchFinalGradeFailure,
    FetchFinalGrades,
    FetchFinalGradesFailure,
    FetchFinalGradesForCurrentUser,
    FetchFinalGradesForCurrentUserFailure,
    FetchFinalGradesForCurrentUserSuccess,
    FetchFinalGradesSuccess,
    FetchFinalGradeSuccess,
    FetchGradesForParticipant,
    FetchGradesForParticipantFailure,
    FetchGradesForParticipantSuccess,
    FetchParticipantFinalGrade,
    SetFinalGrade,
    SetFinalGrades,
    UnassignFinalGradeCertificate,
    UnassignFinalGradeCertificateFailure,
    UnassignFinalGradeCertificateSuccess,
    UnsetFinalGrade,
    UpdateFinalGrade,
    UpdateFinalGradeFailure,
    UpdateFinalGradeSuccess,
} from '../actions';
import { BulkUpdateFinalGradesStatusFailure, BulkUpdateFinalGradesStatusSuccess } from '../actions/index';
import { FinalGradeApiService } from '../services/final-grade.api-service';
import { FinalGradeExtended } from './final-grade-extended.model';
import { FinalGrade } from './final-grade.model';
import { FinalGradeStateModel } from './final-grade.state-model';

@State<FinalGradeStateModel>({
    name: 'finalGrade',
    defaults: new FinalGradeStateModel(),
})
@Injectable()
export class FinalGradeState extends EntityState<FinalGrade> implements IEntityState<FinalGrade> {
    @Selector([GradingSchemeState.getEntity])
    static getEntity(state: FinalGradeStateModel, gradingScheme: GradingScheme, id: string = state.item): FinalGrade {
        if (!state.map) {
            return null;
        }

        const item = state.map.get(id) || null;

        if (item && gradingScheme) {
            for (let i = 0; i < gradingScheme.grades.length; i++) {
                const grade = gradingScheme.grades[i];
                if (grade.id === item.gradeId) {
                    return Object.assign<FinalGrade, FinalGrade, Partial<FinalGrade>>(new FinalGrade(), item, { grade });
                }
            }
        }

        return item;
    }

    @Selector()
    static getEntitiesAsMap(state: FinalGradeStateModel): Map<string, FinalGrade> {
        if (!state.map) {
            return null;
        }

        return state.map;
    }

    @Selector([GradingSchemeState.getEntity])
    static getEntities(state: FinalGradeStateModel, gradingScheme: GradingScheme): FinalGrade[] {
        if (!state.map) {
            return null;
        }

        const stateList = Array.from(state.map.values());

        if (gradingScheme) {
            const gradesContainer = {};

            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });

            return stateList.map<FinalGrade>((finalGrade) =>
                Object.assign<FinalGrade, FinalGrade, Partial<FinalGrade>>(new FinalGrade(), finalGrade, { grade: gradesContainer[finalGrade.gradeId] || null })
            );
        }

        return stateList;
    }

    @Selector([GradingSchemeState.getEntities])
    static getEntitiesWithGradingScheme(state: FinalGradeStateModel, gradingSchemes: GradingScheme[]): FinalGrade[] {
        if (!state.map) {
            return null;
        }

        const stateList = Array.from(state.map.values());

        if (gradingSchemes) {
            const gradesContainer = {};
            gradingSchemes.forEach((gradingScheme) => {
                gradingScheme.grades.forEach((grade) => {
                    gradesContainer[grade.id] = grade;
                });
            });

            return stateList.map<FinalGrade>((finalGrade) =>
                Object.assign<FinalGrade, FinalGrade, Partial<FinalGrade>>(new FinalGrade(), finalGrade, { grade: gradesContainer[finalGrade.gradeId] || null })
            );
        }

        return stateList;
    }

    @Selector()
    static getUserEntities(state: FinalGradeStateModel): FinalGradeExtended[] {
        return state.userFinalGrades;
    }

    constructor(private _finalGradeService: FinalGradeApiService, private _certificateApiService: CertificateApiService) {
        super(_finalGradeService, 0);

        this._propstoSearch = FinalGrade.props_to_search;
    }

    @Action(AddFinalGrades)
    addEntities(ctx: StateContext<FinalGradeStateModel>, action: AddFinalGrades): void {
        this._addEntities(ctx, action);
    }

    @Action(SetFinalGrade)
    setEntity(ctx: StateContext<FinalGradeStateModel>, action: SetFinalGrade): void {
        this._setEntity(ctx, action, AddFinalGrades);
    }

    @Action(UnsetFinalGrade)
    unsetEntity(ctx: StateContext<FinalGradeStateModel>): void {
        this._unsetEntity(ctx);
    }

    createEntity(state: StateContext<FinalGradeStateModel>, action: IActionWithPayload<FinalGrade>): void {
        throw new Error('Method not implemented.');
    }

    @Action(UpdateFinalGrade)
    updateEntity(ctx: StateContext<FinalGradeStateModel>, action: UpdateFinalGrade): void {
        this._updateEntity(ctx, action, UpdateFinalGradeSuccess, UpdateFinalGradeFailure, AddFinalGrades).then(() => {});
    }

    @Action(DeleteFinalGrade)
    deleteEntity(ctx: StateContext<FinalGradeStateModel>, action: DeleteFinalGrade): void {
        this._deleteEntity(ctx, action, DeleteFinalGradeSuccess, DeleteFinalGradeFailure).then(() => {});
    }

    @Action(FetchFinalGrade)
    fetchEntity(ctx: StateContext<FinalGradeStateModel>, action: FetchFinalGrade): void {
        this._fetchEntity(ctx, action, FetchFinalGradeSuccess, FetchFinalGradeFailure, AddFinalGrades, SetFinalGrade).then(() => {});
    }

    @Action(FetchParticipantFinalGrade)
    fetchParticipantFinalGrade({ dispatch }: StateContext<FinalGradeStateModel>, { payload }: FetchParticipantFinalGrade): void {
        this._finalGradeService.readByModuleId(payload).subscribe(
            (entity) => {
                dispatch([new SetFinalGrade(entity), new FetchFinalGradeSuccess(entity)]);
            },
            (error: HttpErrorResponse) => {
                dispatch(new FetchFinalGradeFailure({ entityId: payload, response: error }));
            },
            () => {
                this.setExpirationTime(payload);
            }
        );
    }

    @Action(FetchFinalGradeSuccess)
    fetchEntitySuccess(ctx: StateContext<FinalGradeStateModel>, action: FetchFinalGradeSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchFinalGradeFailure)
    fetchEntityFailure(ctx: StateContext<FinalGradeStateModel>, action: FetchFinalGradeFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetFinalGrades)
    setEntities(ctx: StateContext<FinalGradeStateModel>, action: SetFinalGrades): void {
        this._setEntities(ctx, action);
    }

    @Action(FetchFinalGrades)
    fetchEntities(ctx: StateContext<FinalGradeStateModel>): void {
        this._fetchEntities(ctx, FetchFinalGradesSuccess, FetchFinalGradesFailure, AddFinalGrades).then(() => {});
    }

    @Action(FetchFinalGradesSuccess)
    fetchEntitiesSuccess(ctx: StateContext<FinalGradeStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchFinalGradesFailure)
    fetchEntitiesFailure(ctx: StateContext<FinalGradeStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }

    @Action(BulkUpdateFinalGrades)
    bulkUpdateFinalGrades({ dispatch }: StateContext<FinalGradeStateModel>, { payload }: BulkUpdateFinalGrades): void {
        this._finalGradeService.bulkUpdate(payload).subscribe(
            (entities) => {
                dispatch([new AddFinalGrades(entities), new BulkUpdateFinalGradesSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new BulkUpdateFinalGradesFailure(null));
            }
        );
    }

    @Action(BulkUpdateFinalGradesStatus)
    bulkUpdateFinalGradesStatus({ dispatch }: StateContext<FinalGradeStateModel>, { payload }: BulkUpdateFinalGradesStatus): void {
        this._finalGradeService.bulkUpdateStatus(payload.entities, payload.status).subscribe(
            (entities) => {
                dispatch([new AddFinalGrades(entities), new BulkUpdateFinalGradesStatusSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new BulkUpdateFinalGradesStatusFailure(null));
            }
        );
    }

    @Action(FetchFinalGradesForCurrentUser)
    fetchFinalGradesForCurrentUser({ dispatch, patchState }: StateContext<FinalGradeStateModel>): void {
        this._finalGradeService.getUserFinalGrades().subscribe(
            (entities) => {
                patchState({
                    userFinalGrades: entities,
                });

                dispatch(new FetchFinalGradesForCurrentUserSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchFinalGradesForCurrentUserFailure(null));
            }
        );
    }

    @Action(FetchGradesForParticipant)
    fetchGradesForCurrentUser({ dispatch, patchState }: StateContext<FinalGradeStateModel>, { payload }: FetchGradesForParticipant): void {
        this._finalGradeService.getGradesForParticipant(payload.moduleId).subscribe(
            (entities) => {
                if (entities) {
                    const gradedUnitsConatiner: { [key: string]: GradedUnit } = {};

                    if (entities.partial_grades.length) {
                        entities.partial_grades.forEach((query) => {
                            if (!gradedUnitsConatiner[query.graded_unit.id]) {
                                const gradedUnitQuery = query.graded_unit as IGradedUnitQuery;

                                gradedUnitsConatiner[query.graded_unit.id] = new GradedUnit(gradedUnitQuery);
                            }
                        });
                    }

                    const gradedUnits = Object.keys(gradedUnitsConatiner).map((gradedUnitId) => gradedUnitsConatiner[gradedUnitId]);

                    const partialGrades: PartialGrade[] = [];
                    if (entities.partial_grades) {
                        entities.partial_grades.map((partialGrade) => {
                            partialGrade.participant = entities.participant;
                            partialGrades.push(new PartialGrade(partialGrade));
                        });
                    }

                    if (entities.final_grade) {
                        entities.final_grade.participant = entities.participant;
                        let finalGrade = new FinalGrade(entities.final_grade);
                        dispatch(new SetFinalGrade(finalGrade));
                    }

                    dispatch([new AddGradedUnits(gradedUnits), new SetParticipantPartialGrades(partialGrades), new FetchGradesForParticipantSuccess()]);
                }
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchGradesForParticipantFailure());
            }
        );
    }

    @Action(AssignFinalGradeCertificate)
    assignFinalGradeCertificate({ dispatch }: StateContext<FinalGradeStateModel>, { payload }: AssignFinalGradeCertificate): void {
        this._certificateApiService.assign(payload).subscribe(
            (entity) => {
                const temp = Object.assign(new FinalGrade(), payload, {
                    certificate: {
                        id: entity.id,
                        name: entity.name,
                    },
                });
                dispatch([new AddFinalGrades([temp]), new AssignFinalGradeCertificateSuccess(entity)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new AssignFinalGradeCertificateFailure({ response }));
            }
        );
    }

    @Action(UnassignFinalGradeCertificate)
    unassignFinalGradeCertificate({ dispatch }: StateContext<FinalGradeStateModel>, { payload }: UnassignFinalGradeCertificate): void {
        this._certificateApiService.unassign(payload).subscribe(
            () => {
                const temp = Object.assign(new FinalGrade(), payload, {
                    certificate: null,
                });
                dispatch([new AddFinalGrades([temp]), new UnassignFinalGradeCertificateSuccess()]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new UnassignFinalGradeCertificateFailure({ response }));
            }
        );
    }

    @Action(BulkAssignFinalGradeCertificates)
    bulkAssignFinalGradeCertificates({ dispatch }: StateContext<FinalGradeStateModel>, { payload }: BulkAssignFinalGradeCertificates): void {
        this._certificateApiService.bulkAssign(payload).subscribe(
            (entity) => {
                dispatch(new BulkAssignFinalGradeCertificatesSuccess(entity.id));
            },
            (response: HttpErrorResponse) => {
                dispatch(new BulkAssignFinalGradeCertificatesFailure({ response }));
            }
        );
    }

    @Action(BulkUnassignFinalGradeCertificates)
    bulkUnassignFinalGradeCertificates({ dispatch }: StateContext<FinalGradeStateModel>, { payload }: BulkUnassignFinalGradeCertificates): void {
        this._certificateApiService.bulkUnassign(payload).subscribe(
            (entity) => {
                dispatch(new BulkUnassignFinalGradeCertificatesSuccess(entity.id));
            },
            (response: HttpErrorResponse) => {
                dispatch(new BulkUnassignFinalGradeCertificatesFailure({ response }));
            }
        );
    }
}
